'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { MonoIcon } from '@/components/ui/MonoIcon';
import { ticketApi } from '@/lib/api/ticket/ticketApi';
import type { TicketResponse, TicketThread } from 'dico-types';
import { toast } from 'sonner';
import Loader from '@/components/ui/loader';

interface TicketChatInterfaceProps {
  workspaceId: string;
  ticketId: string;
  thread: TicketThread;
  /** Called when a draft is created. */
  onDraftCreated?: (response: TicketResponse) => void;
  /** Called with the published response after a successful send. */
  onResponsePublished?: (response: TicketResponse) => void;
}

export function TicketChatInterface({
  workspaceId,
  ticketId,
  thread,
  onDraftCreated,
  onResponsePublished
}: TicketChatInterfaceProps) {
  const [message, setMessage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerateDraft = async () => {
    setIsGenerating(true);

    try {
      const { data: draft } = await ticketApi.createTicketResponse(workspaceId, ticketId, {
        channelId: thread.channelId,
        threadId: thread.threadId || null,
        source: thread.source,
        ...(message.trim() && { content: message })
      });

      if (!draft) throw new Error('Failed to create draft');

      setMessage('');
      if (draft) onDraftCreated?.(draft);
      toast.success('Draft created successfully');
    } catch (err) {
      const e = err as { data?: { error?: string }; message?: string };
      toast.error(e?.data?.error || e?.message || 'Failed to create draft');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="">
      <div className="flex flex-col gap-2">
        <textarea
          className="bg-input/30 text-foreground placeholder:text-muted-foreground focus:ring-ring min-h-[80px] w-full resize-none rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2"
          placeholder={`Reply to this ${thread.source} thread… (leave empty to auto-generate)`}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={isGenerating}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handleGenerateDraft();
            }
          }}
        />

        <div className="flex items-center justify-between">
          <Button onClick={handleGenerateDraft} disabled={isGenerating}>
            {isGenerating ? (
              <>
                <Loader className="animate-spin" />
                Generating…
              </>
            ) : (
              <>
                <MonoIcon type="Sparkles" />
                {message.trim() ? 'Create draft' : 'Generate AI draft'}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
